import * as React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Field, FieldProps } from 'formik';

import { Row, Col } from 'antd/lib/grid';

import Divider from 'antd/lib/divider';

import Typography from 'antd/lib/typography';

import { FormikInput } from '@common/react/components/Forms/FormikInput/FormikInput';

import { Nullable } from '@common/typescript/objects/Nullable';

import { PriceType } from '@app/objects/Price';
import { FormValues } from '@app/components/Pages/ClinicPetEditor/types';
import { getCardFields, Card } from '@app/components/UI/Cards/Card';
import { DiscountSelect } from '@app/components/UI/Inputs/DiscountSelect';
import { PhoneInput } from '@app/components/UI/Inputs/PhoneInput';
import { BaseSectionProps, getOwnerInfoFields } from '@app/components/Pages/ClinicPetEditor/ClinicPetsComponents/ViewContent';
import { ApplicationState } from '@app/store';
import { FormikField } from '@app/components/Forms/FormikField';
import { PostalCodeAutocomplete } from '@app/components/UI/Inputs/Address/PostalCodeAutocomplete';
import { PostalCode } from '@app/objects/PostalCode';
import { RegionSelect } from '@app/components/UI/Inputs/Address/RegionSelect';
import { Clinic } from '@app/objects/Clinic';
import { addressService } from '@app/services/AddressService';

type ColumnProps = Pick<React.PropsWithChildren<unknown>, 'children'>;
const Column: React.FC<ColumnProps> = (props: ColumnProps) => <Col xs={24} lg={8}>{props.children}</Col>;

interface OwnerSectionProps extends BaseSectionProps {
	clinic: Nullable<Clinic>;
	required: boolean;
}

export const OwnerDetails: React.VFC<OwnerSectionProps> = (props: OwnerSectionProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'editors.clinic-pet-editor.cards.owner-info' });
	const country = props.clinic?.country?.alpha3 ?? 'unknown';
	const details = addressService.provide(country);

	return (
		<Card
			name="Owner"
			mode={props.mode}
			fullWidth

			editContent={(
				<>
					<Row gutter={[30, 16]}>
						<Column>
							<Field name="ownerFirstName">
								{(fieldProps: FieldProps<FormValues>) => (
									<FormikInput
										fieldProps={fieldProps}
										title={t('owner-first-name')}
										containerClassName=""
									/>
								)}
							</Field>
						</Column>
						<Column>
							<Field name="ownerLastName">
								{(fieldProps: FieldProps<FormValues>) => (
									<FormikInput
										fieldProps={fieldProps}
										title={`${t('owner-last-name')}*`}
										containerClassName=""
									/>
								)}
							</Field>
						</Column>
						<Column>
							<Field name="ownerEmail">
								{(fieldProps: FieldProps<FormValues>) => (
									<FormikInput
										fieldProps={fieldProps}
										title={t('owner-email')}
										containerClassName=""
									/>
								)}
							</Field>
						</Column>
						<Column>
							<Field name="ownerPhone">
								{(fieldProps: FieldProps<FormValues>) => (
									<FormikInput
										fieldProps={fieldProps}
										title={t('owner-phone')}
										containerClassName=""
										render={({ field, form }: FieldProps<string, FormValues>) => {
											const country = useSelector((state: ApplicationState) => state.clinic.item)?.country;

											return <PhoneInput {...field} phoneCode={country?.phoneCode} onChange={(val: string) => form.setFieldValue(field.name, val)} />;
										}}
									/>
								)}
							</Field>
						</Column>
						<Column>
							<Field name="discountId">
								{(fieldProps: FieldProps<FormValues>) => (
									<FormikInput
										fieldProps={fieldProps}
										title={t('discount')}
										containerClassName=""
										render={({ field, form }: FieldProps<number | null, FormValues>) => (
											<DiscountSelect
												allowClear
												disabled={
													props.crematoryId < 1 || fieldProps.form.values.serviceType === undefined || fieldProps.form.values.serviceType < 0
												}
												priceType={PriceType.Wholesale}
												serviceType={form.values.serviceType!}
												value={field.value ?? undefined}
												onChange={(value) => form.setFieldValue(field.name, value ?? null)}
												placeholder="Select the Discount"
											/>
										)}
									/>
								)}
							</Field>
						</Column>
					</Row>
					{props.clinic && props.clinic.showOwnerAddress && (
						<>
							<Divider orientation="left">
								<Typography>Owner Address</Typography>
							</Divider>
							<Row gutter={[30, 16]}>
								<Column>
									<FormikField fieldName="ownerAddress" title={`${t('owner-address-1')}`} />
								</Column>
								<Column>
									<FormikField fieldName="ownerAddress2" title={t('owner-address-2')} />
								</Column>
								<Column>
									<FormikField
										fieldName="ownerZip"
										title={`${t(`owner-${details.mailKey}`)}`}
										render={(fieldProps: FieldProps<string, FormValues>) => (
											<PostalCodeAutocomplete
												value={fieldProps.field.value}
												onChange={(value: string) =>
													fieldProps.form.setFieldValue(fieldProps.field.name as keyof FormValues, value, false)}
												onSelect={(value, item?: PostalCode) => {
													if (!item) return;

													fieldProps.form.setValues({
														...fieldProps.form.values,
														ownerZip: item.zip,
														ownerCity: item.city,
														ownerStateId: item.stateId,
													});
												}}
												country={country}
											/>
										)}
									/>
								</Column>
								<Column>
									<FormikField
										fieldName="ownerCity"
										title={`${t('owner-city')}`}
									/>
								</Column>
								<Column>
									<FormikField
										fieldName="ownerStateId"
										title={`${t(`owner-${details.regionKey}`)}`}
										render={({ field, form }) => (
											<RegionSelect
												value={field.value}
												onChange={(state?: number) => {
													form.setFieldValue(field.name, state);
													form.setFieldValue('ownerState', null);
												}}

												country={country}
											/>
										)}
									/>
								</Column>
							</Row>
						</>
					)}
				</>
			)}

			viewContent={(
				<Field name="">
					{({ form }: FieldProps<FormValues>) => getCardFields(getOwnerInfoFields({ pet: form.values, t, clinic: props.clinic }))}
				</Field>
			)}
		/>
	);
};
